// @flow

import React from "react";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import { SchemaForm, Button } from "@components";
import type { StepProps } from "@components";
import { emailPattern } from "@utils/schema";

type OwnProps = $Exact<{
  editing: boolean,
  schema: Object,
  data: $Exact<{
    name: string,
    description: string,
    email: string,
    employeeReferrals: boolean,
    isActive: boolean,
    managerReferrals: boolean,
    manualReferrals: boolean,
    serviceProvided: string,
  }>,

  onRef: Object,
  nextButtonLabel: string,
  hideButton: boolean,
  onChange: (props: Object) => void,
  onSubmit: (props: Object) => void,
  t: () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepSupplierDetails extends React.Component<Props> {
  validator: *;
  errorMessage: *;

  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef();
  }

  get data() {
    return this.props.data;
  }

  get uiSchema() {
    return {
      isActive: {
        "ui:title": this.props.t("STATUS"),
        "ui:widget": "toggle",
        "ui:options": {
          labels: [
            { label: this.props.t("ACTIVE"), value: true },
            { label: this.props.t("INACTIVE"), value: false },
          ],
        },
      },
      name: {
        "ui:title": this.props.t("SUPPLIER_NAME"),
      },
      description: {
        "ui:title": this.props.t("SUPPLIER_DESCRIPTION"),
        "ui:widget": "textarea",
        "ui:options": {
          rows: 4,
        },
      },
      serviceProvided: {
        "ui:title": this.props.t("SERVICE_PROVIDED"),
        "ui:widget": "textarea",
        "ui:options": {
          rows: 4,
        },
      },
      email: {
        "ui:title": this.props.t("EMAIL_ADDRESS"),
      },
      managerReferrals: {
        "ui:title": this.props.t("MANAGER_REFERRALS"),
        "ui:widget": "toggle",
        "ui:options": {
          labels: [
            { label: this.props.t("ACTIVE"), value: true },
            { label: this.props.t("INACTIVE"), value: false },
          ],
        },
      },
      employeeReferrals: {
        "ui:title": this.props.t("EMPLOYEE_REFERRALS"),
        "ui:widget": "toggle",
        "ui:options": {
          labels: [
            { label: this.props.t("ACTIVE"), value: true },
            { label: this.props.t("INACTIVE"), value: false },
          ],
        },
      },
      manualReferrals: {
        "ui:title": this.props.t("MANUAL_REFERRALS"),
        "ui:widget": "toggle",
        "ui:options": {
          labels: [
            { label: this.props.t("ACTIVE"), value: true },
            { label: this.props.t("INACTIVE"), value: false },
          ],
        },
      },
    };
  }

  componentDidMount() {
    this.validator = new Ajv().compile(this.props.schema);
  }

  get incorrectEmailPattern() {
    return this.data.email && !emailPattern.test(this.data.email);
  }

  isInvalid() {
    let error = false;

    if (this.validator) {
      this.validator(removeNullAndUndefinedProps(this.data));
      error = ((this.validator.errors || [])[0] || {}).message;
      if (error) return error;
      else if (!this.data.email || !emailPattern.test(this.data.email)) return true;
    }

    return false;
  }

  submitForm = () => {
    this.formRef.current.submit();
  };

  submitData = (formData: Object) => {
    const data = { ...this.data, ...removeNullAndUndefinedProps(formData) };
    if (this.props.handleSubmit) {
      this.props.handleSubmit(data, true);
    }
  };

  updateData = (formData: Object) => {
    this.props.onChange(formData, !!this.isInvalid());
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <SchemaForm
          ref={this.formRef}
          schema={this.props.schema}
          uiSchema={this.uiSchema}
          formData={this.data}
          externalSubmit
          onChange={this.updateData}
          onSubmit={this.submitData}
        />
        {this.incorrectEmailPattern && (
          <p className={"w-100 d-flex justify-content-center mt-2 mb-2 alert alert-danger"}>
            {this.props.t("INVALID_EMAIL_ADDRESS")}
          </p>
        )}
        {!this.props.isCollapsable && (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.submitForm} disabled={!!this.isInvalid()}>
              {this.props.nextButtonLabel}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default localize()(StepSupplierDetails);
